import { useState } from "react";
import { toast } from "sonner";
import { http, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Eye, EyeSlash, Key } from "@phosphor-icons/react";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState({ cur: false, next: false, conf: false });
  const [saving, setSaving] = useState(false);

  const reset = () => setForm({ current_password: "", new_password: "", confirm: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.current_password) { toast.error("Enter your current password"); return; }
    if (form.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (form.new_password !== form.confirm) { toast.error("New passwords do not match"); return; }
    setSaving(true);
    try {
      await http.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <div className="label-overline text-blue-600 mb-1 flex items-center gap-1.5">
            <Key size={12} weight="bold" /> Account Security
          </div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
            Change Password
          </DialogTitle>
          <DialogDescription className="text-zinc-600">
            Enter your current password and pick a new one (6+ characters).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="change-password-form">
          <PasswordField
            label="Current Password *"
            testId="cp-current"
            value={form.current_password}
            onChange={(v) => setForm({ ...form, current_password: v })}
            visible={show.cur}
            onToggle={() => setShow((s) => ({ ...s, cur: !s.cur }))}
          />
          <PasswordField
            label="New Password *"
            testId="cp-new"
            value={form.new_password}
            onChange={(v) => setForm({ ...form, new_password: v })}
            visible={show.next}
            onToggle={() => setShow((s) => ({ ...s, next: !s.next }))}
          />
          <PasswordField
            label="Confirm New Password *"
            testId="cp-confirm"
            value={form.confirm}
            onChange={(v) => setForm({ ...form, confirm: v })}
            visible={show.conf}
            onToggle={() => setShow((s) => ({ ...s, conf: !s.conf }))}
          />
          <div className="flex justify-end gap-2 pt-3 border-t border-zinc-300">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                    className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="cp-submit"
                    className="rounded-none bg-blue-600 hover:bg-blue-700 text-white font-mono uppercase tracking-widest text-xs">
              {saving ? "Saving…" : "Update Password"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PasswordField({ label, value, onChange, visible, onToggle, testId }) {
  return (
    <div>
      <label className="label-overline block mb-1 text-zinc-700">{label}</label>
      <div className="relative">
        <Input
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          data-testid={testId}
          className="rounded-none border-zinc-400 pr-9 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
        />
        <button
          type="button"
          onClick={onToggle}
          data-testid={`${testId}-toggle`}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-900"
          aria-label={visible ? "Hide password" : "Show password"}
        >
          {visible ? <EyeSlash size={14} weight="bold" /> : <Eye size={14} weight="bold" />}
        </button>
      </div>
    </div>
  );
}
